import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'Vendor/Store/store';
import { saveCreation } from 'Vendor/Store/studio/thunk';

interface UseAutoSaveCreationOptions {
    creationId?: number;
    debounceMs?: number;
    intervalMs?: number;
}

export function useAutoSaveCreation({
    creationId,
    debounceMs = 2500,
    intervalMs = 30000,
}: UseAutoSaveCreationOptions) {
    const dispatch = useDispatch<AppDispatch>();
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);

    // Debounced save on creationId changes
    useEffect(() => {
        if (!creationId) return;

        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            dispatch(saveCreation());
            debounceTimer.current = null;
        }, debounceMs);

        return () => {
            // Force save pending changes on unmount
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
                dispatch(saveCreation());
            }
        };
    }, [creationId, dispatch, debounceMs]);

    // Interval save for long sessions
    useEffect(() => {
        if (!creationId) return;

        const interval = setInterval(() => {
            dispatch(saveCreation());
        }, intervalMs);

        return () => clearInterval(interval);
    }, [creationId, dispatch, intervalMs]);

    // Force save manually (optional, e.g., on back button)
    const forceSave = async () => {
        try {
            await dispatch(saveCreation()).unwrap();
        } catch (err) {
            console.error('Failed to save creation:', err);
        }
    };

    return { forceSave };
}
